import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useFeature } from '../featureFlags/FeatureGate';
import {
  cellDiffBasis,
  cellDiffText,
  distanceNote,
  eurPerKmNumber,
  FEATURE_PRICE_STATISTICS,
  levelDetail,
  priceComparisonOf,
  type PriceComparison,
} from './priceStatistics';

/**
 * Statystyka cen z VIA przy zleceniu (13.10.2026, przełącznik
 * `priceStatistics`): zdanie na karcie i nad rekomendacjami, komórka „€/km”
 * na liście. Liczby i zdanie przychodzą z API; tu — tylko pokazanie, bez
 * dopracowania stylów (ustalenie 13.10.2026). Bez przełącznika — nic.
 */

/** Szczegóły w dymku: poziomy z rozrzutem, kurs i skąd liczby. */
const Details = ({ comparison }: { comparison: PriceComparison }) => (
  <Stack spacing={0.5} sx={{ py: 0.5 }}>
    {distanceNote(comparison) && (
      <Typography variant="caption" component="div" data-testid="price-distance-note">
        {distanceNote(comparison)}
      </Typography>
    )}
    {comparison.levels.map((l) => (
      <Typography key={l.level} variant="caption" component="div">
        {levelDetail(l)}
      </Typography>
    ))}
    {comparison.rateNote && (
      <Typography variant="caption" component="div">
        Kurs NBP: {comparison.rateNote}
      </Typography>
    )}
    <Typography variant="caption" component="div">
      {comparison.basis}
    </Typography>
  </Stack>
);

/** Zdanie porównania (karta, rekomendacje), z kosztem własnym, gdy jest. */
export const PriceStatisticsText = ({
  comparison,
}: {
  comparison: PriceComparison | null;
}) => {
  const on = useFeature(FEATURE_PRICE_STATISTICS);
  if (!on || !comparison) return null;
  return (
    <Tooltip title={<Details comparison={comparison} />}>
      <Typography variant="body2" component="span" data-testid="price-statistics">
        {comparison.text}
        {comparison.ownCost ? ` · ${comparison.ownCost.text}` : ''}
      </Typography>
    </Tooltip>
  );
};

/** Pole `priceStatistics` z rekordu karty. */
export const RecordPriceStatistics = ({ record }: { record: Record<string, unknown> }) => (
  <PriceStatisticsText comparison={priceComparisonOf(record)} />
);

/**
 * Komórka listy „€/km”: cena klienta za km (sama liczba — jednostkę niesie
 * nagłówek) i pod nią sama różnica wobec mediany („−8%”); wobec czego,
 * zdanie i poziomy — w dymku. Wąska, bo lista z „Giełdą” ma się mieścić przy
 * 1366 px (08.10.2026; poprawka po stagingu 14.10.2026 — bez skrótów
 * „rel.”/„kl.”, ok. 33 px treści zamiast 55). „za mało” może się złamać
 * na dwie linie — nie wyznacza szerokości kolumny.
 */
export const PricePerKmCell = ({ record }: { record: Record<string, unknown> }) => {
  const c = priceComparisonOf(record);
  if (!c) return null;
  const diff = cellDiffText(c);
  const diffBasis = cellDiffBasis(c);
  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="body2">{c.text}</Typography>
          {diffBasis && (
            <Typography variant="caption" component="div">
              {diffBasis}
            </Typography>
          )}
          <Details comparison={c} />
        </Box>
      }
    >
      <Box data-testid="price-per-km">
        <Typography variant="body2" component="div" sx={{ whiteSpace: 'nowrap' }}>
          {c.eurPerKm === null ? '—' : eurPerKmNumber(c.eurPerKm)}
        </Typography>
        {diff && (
          <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {diff}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};
